import styles from "./StockCommodity.module.scss";
import { FC, useState, useEffect, useMemo, useRef, type JSX } from "react";
import { useChartSeriesToggle } from "src/hooks/useChartSeriesToggle";
import { Stock } from "@/models/stock";
import { classNames } from "@/lib/utils";
import { getStockCommodityExposure, getStockCommodityMoves, getStockCommodityMovesForScatterPlot } from "@/controllers/stock-commodity";
import Module from "@/components/module/Module";
import { useAtomValue } from "jotai";
import { backgroundColorState, greyState, lighterBackgroundColorState, textColorState, themeState } from "@/lib/store";
import StockCommodityMovesChart from "../stock-commodity-moves/StockCommodityMovesChart";
import StockCommodityScatterPlotChart, { type ScatterSeries } from "../StockCommodityScatterPlotChart/StockCommodityScatterPlotChart";
import type { TimeSeries } from "@/components/ortex-charts/TimeSeriesChart";
import { DataGridNews } from "../data-grid-news/DataGridNews";
import { DataGrid } from "../data-grid/DataGrid";
import { Box } from "@mui/material";

interface PropsTypes {
    stock: Stock;
}

const rowKey = "commodity";

type tableRow = {
    commodity: string;
    "last trading day": any[];
    "1 week": any[];
    sensitivity: any[];
    "effect score(1w)": any[];
    "show on chart": any[];
    code: number;
};

const StockCommodity: FC<PropsTypes> = ({ stock }) => {
    const theme = useAtomValue(themeState);
    const newsOptions = { stock_news: true };

    const [commodityExpFilters, setCommodityExpFilters] = useState<JSX.Element>();
    const [obfuscated, setObfuscated] = useState<boolean>(false);

    const grey = useAtomValue(greyState);
    const textColor = useAtomValue(textColorState);
    const lighterBackgroundColor = useAtomValue(lighterBackgroundColorState);
    const backgroundColor = useAtomValue(backgroundColorState);

    const [scatterChartSeries, setScatterChartSeries] = useState<ScatterSeries[]>([]);
    // The scatter series each commodity row added, so its removal takes exactly that one away.
    const scatterIdsRef = useRef<Map<string, string>>(new Map());

    // The grid keeps the first render's copy of this button; see useChartSeriesToggle.
    const { series, toggle, renderIcon } = useChartSeriesToggle(stock.tradingitem_id);
    const chartButton = useMemo(
        () => ({
            onClick: (_value: number | string, row: tableRow) => {
                const key = String(row[rowKey]);
                return toggle(
                    key,
                    async () => {
                        const [data, scatterPlotData] = await Promise.all([getStockCommodityMoves(row.code), getStockCommodityMovesForScatterPlot(row.commodity, stock.stock_id)]);
                        const lines = Object.entries((data?.index ?? {}) as Record<string, [number, number][]>).map(([name, points]): TimeSeries => ({ id: name, title: name, type: "line", data: points, axis: "left", precision: 2 }));
                        if (lines.length) {
                            const scatterId = String(scatterPlotData?.names[0]);
                            scatterIdsRef.current.set(key, scatterId);
                            setScatterChartSeries((prev) => [...prev.filter((el) => el.id !== scatterId), { id: scatterId, name: scatterId, data: scatterPlotData?.moves ?? [] }]);
                        }
                        return lines;
                    },
                    () => {
                        const scatterId = scatterIdsRef.current.get(key);
                        scatterIdsRef.current.delete(key);
                        setScatterChartSeries((prev) => prev.filter((el) => el.id !== scatterId));
                    }
                );
            },
            renderIcon: (_value: number | string, row: tableRow) => renderIcon(String(row[rowKey])),
        }),
        [toggle, renderIcon, stock.stock_id]
    );

    const getCommoditySeries = () => {
        getStockCommodityMovesForScatterPlot("OIL CL", stock.stock_id).then((scatterPlotData) => {
            setObfuscated(scatterPlotData?.obfuscated);
            setScatterChartSeries([{ id: String(scatterPlotData?.names[0]), name: String(scatterPlotData?.names[0]), data: scatterPlotData?.moves ?? [] }]);
        });
    };

    useEffect(() => {
        getCommoditySeries();
    }, []);

    return (
        <div className={classNames(styles[theme], styles.StockCommodity)}>
            <div className={styles.table}>
                <Module title="Commodity Exposure" help={{ moduleId: "commodity_exposure", modulePage: "V2_stock_commodities" }} allowFullscreen filters={commodityExpFilters}>
                    <DataGrid id={stock.stock_id} fetcher={getStockCommodityExposure} button={chartButton} setFilters={setCommodityExpFilters} />
                </Module>
            </div>
            <div className={styles.news}>
                <Module title="News" help={{ moduleId: "morning_note", modulePage: "V2_markets_overview" }} noPadding={{ source: "body", type: "all" }}>
                    <div className={styles["news-list"]}>
                        <div>
                            <DataGridNews id={stock.company_id} options={{ ...newsOptions, type: "company" }} />
                        </div>
                    </div>
                </Module>
            </div>
            <Box className={styles.chart}>
                <Module title={`${stock.exchangesymbol}:${stock.ticker} Commodity Moves`} help={{ moduleId: "commodity_moves_chart", modulePage: "V2_stock_commodities" }} noPadding={{ source: "body", type: "mobile" }} feedback>
                    <StockCommodityMovesChart stock={stock} extraSeries={series} />
                </Module>
            </Box>
            <Box className={styles.chart}>
                <Module title={`${stock.exchangesymbol}:${stock.ticker} Commodity Moves Scatter Plot`} help={{ moduleId: "commodity_moves_chart", modulePage: "V2_stock_commodities" }} noPadding={{ source: "body", type: "mobile" }}>
                    <StockCommodityScatterPlotChart series={scatterChartSeries} obfuscated={obfuscated} />
                </Module>
            </Box>
        </div>
    );
};

export default StockCommodity;
